import {
  type CompanyId,
  type SmallUser,
  type TinyRole,
  type TinyUser,
  toSmallUser,
  type UserData,
  type UserId,
  type UserProfileData,
  type UserPreferences,
  type UserProfileSuccess,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { type UserList } from '../types'
import { profileKey, tinyUserKey, userKey, userListKey } from '../keys'
import { BLACK_BLURHASH } from '../avatar'
import { invalidateUserListQueries, syncListedUserRoles } from './listRoles'

type UserCache = {
  setProfile: (user: UserProfileData, userId?: UserId) => void
  updateAvatar: (userId: UserId | undefined) => void
  setProfileNotificationPrefs: (userId: UserId, notificationPrefs: UserPreferences) => void
  clearProfile: (userId?: UserId) => void
  clearItem: (userId: UserId) => void
  clearList: (companyId?: CompanyId) => void
  clearAllLists: () => void
  syncListedRoles: (userId: UserId, update: (roles: TinyRole[]) => TinyRole[]) => void
  getItem: (userId: UserId) => UserData | undefined
  addUser: (user: UserData, companyId?: CompanyId) => void
  setTinyUser: (user: TinyUser) => void
  setUser: (user: UserData) => void
  getTinyUser: (userId: UserId | undefined) => TinyUser | undefined
  getUser: (userId?: UserId) => UserData | undefined
}

export function useUserCache(): UserCache {
  const queryClient = useQueryClient()

  const setTinyUser = (user: TinyUser): void => {
    queryClient.setQueryData<TinyUser>(tinyUserKey(user.userId), user)
  }

  const setUser = (user: UserData): void => {
    setTinyUser(user)
    queryClient.setQueryData<UserData>(userKey(user.userId), user)
  }

  const clearUser = (userId: UserId): void => {
    queryClient.invalidateQueries({ queryKey: profileKey(userId) })
    queryClient.invalidateQueries({ queryKey: userKey(userId) })
    queryClient.invalidateQueries({ queryKey: tinyUserKey(userId) })
  }

  const updateAvatar = (userId: UserId | undefined): void => {
    const update = <T extends TinyUser>(user: T): T => ({
      ...user,
      avatarUrl: user.avatarUrl + '?v=' + Date.now(),
      avatarBlurhash: user.avatarBlurhash ?? BLACK_BLURHASH,
    })
    queryClient.setQueryData<UserProfileSuccess>(profileKey(userId), (existing) => {
      if (!existing) return
      return { ...existing, user: update(existing.user) }
    })
    queryClient.setQueryData<UserData>(userKey(userId), (existing) => {
      if (!existing) return
      return update(existing)
    })
    queryClient.setQueryData<TinyUser>(tinyUserKey(userId), (existing) => {
      if (!existing) return
      return update(existing)
    })
    queryClient.setQueriesData<UserList>({ queryKey: userListKey() }, (existing) => {
      if (!existing) return
      return existing.map((user) => (user.userId === userId ? update(user) : user))
    })
  }

  /** `/user/update` responses omit `notificationPrefs`, so the saved value is written into every cached profile of that user. */
  const setProfileNotificationPrefs = (userId: UserId, notificationPrefs: UserPreferences): void => {
    queryClient.setQueriesData<UserProfileSuccess>({ queryKey: profileKey() }, (existing) => {
      if (existing?.user.userId !== userId) return existing
      return { ...existing, user: { ...existing.user, notificationPrefs } }
    })
  }

  const getTinyUser = (userId: UserId | undefined): TinyUser | undefined => {
    let user = queryClient.getQueryData<TinyUser>(tinyUserKey(userId))
    if (!user) {
      user = queryClient.getQueryData<UserData>(userKey(userId))
    }
    if (!user) {
      user = queryClient.getQueryData<UserProfileSuccess>(profileKey(userId))?.user
    }
    if (!user) {
      user = queryClient.getQueryData<UserList>(userListKey())?.find((c) => userId === c.userId)
    }
    return user
  }

  const getUser = (userId?: UserId): UserData | undefined => {
    let user = queryClient.getQueryData<UserData>(userKey(userId))
    if (!user) {
      user = queryClient.getQueryData<UserProfileSuccess>(profileKey(userId))?.user
    }
    if (!user) {
      user = queryClient.getQueryData<UserList>(userListKey())?.find((c) => userId === c.userId)
    }
    return user
  }

  return {
    setProfile: (user: UserProfileData, userId?: UserId) => {
      queryClient.setQueryData<UserProfileSuccess>(profileKey(userId), (previous) => {
        if (!previous) return
        return {
          ...previous,
          user,
        }
      })
      setUser(user)
    },
    updateAvatar,
    setProfileNotificationPrefs,
    clearProfile: (userId?: UserId) => {
      queryClient.invalidateQueries({ queryKey: profileKey(userId) })
    },
    clearItem: clearUser,
    clearList: (companyId?: CompanyId) => {
      queryClient.invalidateQueries({ queryKey: userListKey({ companyId }) })
    },
    clearAllLists: (): void => {
      invalidateUserListQueries(queryClient)
    },
    syncListedRoles: (userId, update): void => {
      syncListedUserRoles({ queryClient, userId, update })
    },
    getItem: (userId: UserId): UserData | undefined => {
      return queryClient.getQueryData<UserData>(userKey(userId))
    },
    addUser: (user: UserData, companyId?: CompanyId) => {
      setUser(user)
      const props = companyId ? { companyId } : undefined
      const smallUser = toSmallUser(user)
      queryClient.setQueriesData<SmallUser[]>({ queryKey: userListKey(props) }, (existing) => {
        if (!existing) return
        return [...existing, smallUser]
      })
    },
    setTinyUser,
    setUser,
    getTinyUser,
    getUser,
  }
}
